'use client';

import {
  TopicItemFragment,
  TopicItemLightFragment,
  TopicSectionFieldsFragment,
} from '@generated/graphql';
import {FC, startTransition, SyntheticEvent, useCallback, useEffect, useMemo, useState} from 'react';
import {Box, CircularProgress, Container, MenuItem, SelectChangeEvent, Typography, useMediaQuery, useTheme} from '@mui/material';
import {
  StyledLoadMoreButton,
  StyledTopicSectionContainer,
  StyledTopicSelect,
  StyledTopicTab,
  StyledTopicTabs,
} from '@/components/sections/topic-section/TopicSection.styles';
import {useTranslations} from 'next-intl';
import {usePathname, useRouter, useSearchParams,} from 'next/navigation';
import TopicCard from '@/components/sections/topic-section/TopicCard';
import {
  StyledTopicCardFirstRowContainer,
  StyledTopicCardGridContainer
} from '@/components/sections/topic-section/TopicCard.styles';
import {fetchMoreTopics, fetchTopicsByCategory} from '@/components/sections/topic-section/actions';
import {topicHasTopicAndLabel} from '@/lib/type-guards';
import {clearSessionCache, readSessionCache, writeSessionCache} from '@/lib/session-storage';

type TopicSectionProps = {
    section: TopicSectionFieldsFragment & { sys: { id: string } };
};

export type TopicItemWithRequiredFields = TopicItemFragment & {
  slug: string;
  topic: {
    label: string;
    topic: string;
  };
  image: {
    url: string;
  }
};

export const topicItemHasRequiredFields = (
  topic: TopicItemLightFragment | null | undefined
): topic is TopicItemWithRequiredFields => {
  return topic != null && !!topic.slug && !!topic.image?.url && !!topic.topic?.label && !!topic.topic?.topic;
};
function a11yProps(value: string) {
  return {
    id: `topic-tab-${value}`,
    'aria-controls': `topic-tabpanel-${value}`,
  };
}

const ALL_VALUE = 'all';
const CATEGORY_INITIAL_SIZE = 8;
const LOAD_MORE_SIZE = 6;
const STORAGE_PREFIX = 'topicSection';

type CachedAll = { extra: TopicItemLightFragment[] };
type CachedCategory = { items: TopicItemLightFragment[]; total: number };

const allCacheKey = (sectionId: string) => `${STORAGE_PREFIX}:${sectionId}:all`;
const categoryCacheKey = (sectionId: string, category: string) =>
  `${STORAGE_PREFIX}:${sectionId}:cat:${category}`;

// Module-level guard so hard-reload cache-clearing only runs on the very first
// mount during this SPA session, not on every back/forward re-mount.
let initialReloadHandled = false;

const TopicSection: FC<TopicSectionProps> = ({ section }) => {
  const t = useTranslations('TopicSection');

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const topics = useMemo(() => {
    return section.topicsCollection?.items ?? [];
  }, [section.topicsCollection]);

  const validTopics = useMemo(
    () => topics.filter(topicHasTopicAndLabel),
    [topics]
  );

  const validTopicValues = useMemo(
    () => new Set(validTopics.map((t) => t.topic)),
    [validTopics]
  );

  // ---- Derive category from URL safely ----
  const categoryFromQueryRaw = searchParams?.get('category');

  const categoryFromQuery =
        categoryFromQueryRaw && validTopicValues.has(categoryFromQueryRaw)
          ? categoryFromQueryRaw
          : ALL_VALUE;

  // ---- Selected tab is derived from the URL ----
  const selected = categoryFromQuery;

  // ---- Load more state ----
  const [extraItems, setExtraItems] = useState<TopicItemLightFragment[]>([]);
  const [categoryItems, setCategoryItems] = useState<TopicItemLightFragment[]>([]);
  const [categoryTotal, setCategoryTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  const locale = useMemo(() => {
    const parts = pathname.split('/').filter(Boolean);
    return ['en', 'sv'].includes(parts[0]) ? parts[0] : 'sv';
  }, [pathname]);

  const fetchCategory = useCallback(async (topic: string) => {
    setCategoryLoading(true);
    setCategoryItems([]);
    setCategoryTotal(0);
    try {
      const data = await fetchTopicsByCategory(section.sys.id, locale, topic, 0, CATEGORY_INITIAL_SIZE);
      const nonNull = data.items.filter((item): item is TopicItemLightFragment => item != null);
      setCategoryItems(nonNull);
      setCategoryTotal(data.total);
    } finally {
      setCategoryLoading(false);
    }
  }, [section.sys.id, locale]);

  // Hydrate previously-loaded items from sessionStorage on mount so back-navigation
  // restores the user's "load more" progress without an extra fetch.
  // On the *first* mount of this SPA session, if the page was hard-reloaded, purge all
  // topicSection caches first so a refresh starts clean. Subsequent re-mounts (back/forward)
  // skip that branch and hydrate normally.
  useEffect(() => {
    if (!initialReloadHandled) {
      initialReloadHandled = true;
      const navEntry = typeof performance !== 'undefined'
        ? (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined)
        : undefined;
      if (navEntry?.type === 'reload') {
        try {
          for (let i = sessionStorage.length - 1; i >= 0; i--) {
            const key = sessionStorage.key(i);
            if (key && key.startsWith(`${STORAGE_PREFIX}:`)) sessionStorage.removeItem(key);
          }
        } catch { /* ignore */ }
      }
    }

    const cachedAll = readSessionCache<CachedAll>(allCacheKey(section.sys.id));
    if (cachedAll && Array.isArray(cachedAll.extra)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Hydration sync from sessionStorage; deferred to post-hydration to avoid SSR mismatch.
      setExtraItems(cachedAll.extra);
    }
    if (categoryFromQuery !== ALL_VALUE) {
      const cachedCat = readSessionCache<CachedCategory>(categoryCacheKey(section.sys.id, categoryFromQuery));
      if (cachedCat && Array.isArray(cachedCat.items) && cachedCat.items.length > 0) {
        setCategoryItems(cachedCat.items);
        setCategoryTotal(cachedCat.total ?? 0);
      }
    }
    setHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch on initial load or back/forward when landing on a category URL.
  // Waits for hydration so we don't refetch when sessionStorage has cached items.
  useEffect(() => {
    if (!hydrated) return;
    if (categoryFromQuery !== ALL_VALUE && categoryItems.length === 0 && !categoryLoading) {
      queueMicrotask(() => fetchCategory(categoryFromQuery));
    }
  }, [categoryFromQuery, fetchCategory, categoryLoading, hydrated, categoryItems.length]);

  // Persist extraItems for the ALL tab.
  useEffect(() => {
    if (!hydrated) return;
    if (extraItems.length === 0) {
      clearSessionCache(allCacheKey(section.sys.id));
    } else {
      writeSessionCache(allCacheKey(section.sys.id), {extra: extraItems} satisfies CachedAll);
    }
  }, [hydrated, extraItems, section.sys.id]);

  // Persist categoryItems for the currently selected category tab.
  useEffect(() => {
    if (!hydrated) return;
    if (selected === ALL_VALUE) return;
    if (categoryItems.length === 0) {
      clearSessionCache(categoryCacheKey(section.sys.id, selected));
    } else {
      writeSessionCache(
        categoryCacheKey(section.sys.id, selected),
        {items: categoryItems, total: categoryTotal} satisfies CachedCategory,
      );
    }
  }, [hydrated, selected, categoryItems, categoryTotal, section.sys.id]);

  const initialItems = useMemo(
    () => section.topicItemsCollection?.items ?? [],
    [section.topicItemsCollection],
  );

  const allTotal = section.topicItemsCollection?.total ?? 0;

  const updateSelection = useCallback((newValue: string) => {
    const params = new URLSearchParams(searchParams?.toString());
    if (newValue === ALL_VALUE) {
      params.delete('category');
    } else {
      params.set('category', newValue);
      fetchCategory(newValue);
    }

    startTransition(() => {
      router.replace(`${pathname}?${params.toString()}`, {
        scroll: false,
      });
    });
  }, [searchParams, pathname, router, fetchCategory]);

  const handleChange = useCallback((_: SyntheticEvent, newValue: string) => {
    updateSelection(newValue);
  }, [updateSelection]);

  const handleSelectChange = useCallback((e: SelectChangeEvent<unknown>) => {
    updateSelection(e.target.value as string);
  }, [updateSelection]);

  const handleLoadMore = useCallback(async () => {
    setLoading(true);
    try {
      if (selected === ALL_VALUE) {
        const skip = initialItems.length + extraItems.length;
        const data = await fetchMoreTopics(section.sys.id, locale, skip, LOAD_MORE_SIZE);
        const nonNull = data.items.filter((item): item is TopicItemLightFragment => item != null);
        setExtraItems((prev) => [...prev, ...nonNull]);
      } else {
        const data = await fetchTopicsByCategory(section.sys.id, locale, selected, categoryItems.length, LOAD_MORE_SIZE);
        const nonNull = data.items.filter((item): item is TopicItemLightFragment => item != null);
        setCategoryItems((prev) => [...prev, ...nonNull]);
        setCategoryTotal(data.total);
      }
    } finally {
      setLoading(false);
    }
  }, [section.sys.id, locale, selected, initialItems.length, extraItems.length, categoryItems.length]);

  const allTranslated = t('tabs.all');

  const isAll = selected === ALL_VALUE;
  const rawDisplayItems = isAll
    ? [...initialItems, ...extraItems].filter(topicItemHasRequiredFields)
    : categoryItems.filter(topicItemHasRequiredFields);
  const seenIds = new Set<string>();
  const displayItems = rawDisplayItems.filter((item) => {
    if (seenIds.has(item.sys.id)) return false;
    seenIds.add(item.sys.id);
    return true;
  });
  const firstItems = displayItems.slice(0, 2);
  const restItems = displayItems.slice(2);

  const hasMore = isAll
    ? (initialItems.length + extraItems.length) < allTotal
    : categoryItems.length < categoryTotal;

  return (
    <Container maxWidth='mdlg' {...(section.id && { id: section.id })}>
      <StyledTopicSectionContainer>
        {section.heading && (
          <Typography variant='h1' sx={{marginBottom: 2}}>{section.heading}</Typography>
        )}

        {validTopics.length > 0 && (
          <Box sx={{ marginBottom: 2, width: isSmallScreen ? '100%' : 'auto' }}>
            {isSmallScreen ? (
              <StyledTopicSelect
                value={selected}
                onChange={handleSelectChange}
              >
                {validTopics.map((topic) => (
                  <MenuItem key={topic.sys.id} value={topic.topic}>
                    {topic.label}
                  </MenuItem>
                ))}
                <MenuItem key={ALL_VALUE} value={ALL_VALUE}>
                  {allTranslated}
                </MenuItem>
              </StyledTopicSelect>
            ) : (
              <StyledTopicTabs
                value={selected}
                onChange={handleChange}
                aria-label='Topic tabs'
              >
                {validTopics.map((topic) => (
                  <StyledTopicTab
                    key={topic.sys.id}
                    value={topic.topic}
                    label={topic.label}
                    {...a11yProps(topic.topic)}
                  />
                ))}

                <StyledTopicTab
                  key={ALL_VALUE}
                  value={ALL_VALUE}
                  label={allTranslated}
                  {...a11yProps(ALL_VALUE)}
                />
              </StyledTopicTabs>
            )}
          </Box>
        )}

        {categoryLoading ? (
          <Box sx={{display: 'flex', justifyContent: 'center', py: 8}}>
            <CircularProgress />
          </Box>
        ) : displayItems.length > 0 && (
          <>
            {firstItems.length > 0 && (
              <StyledTopicCardFirstRowContainer>
                {firstItems.map((item) => (
                  <TopicCard
                    href={item.slug}
                    heading={item.heading}
                    imageUrl={item.image.url}
                    key={item.sys.id}
                    topic={item.topic.label}
                    date={item.createdAt}
                    imageAlt={item.image.title}
                  />
                ))}
              </StyledTopicCardFirstRowContainer>
            )}

            {restItems.length > 0 && (
              <StyledTopicCardGridContainer>
                {restItems.map((item) => (
                  <TopicCard
                    href={item.slug}
                    heading={item.heading}
                    imageUrl={item.image.url}
                    key={item.sys.id}
                    topic={item.topic.label}
                    date={item.createdAt}
                    imageAlt={item.image.title}
                    focalX={item.focalX}
                    focalY={item.focalY}
                  />
                ))}
              </StyledTopicCardGridContainer>
            )}
          </>
        )}

        {hasMore && !categoryLoading && (
          <StyledLoadMoreButton
            variant='contained'
            onClick={handleLoadMore}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} color='inherit' /> : t('loadMore')}
          </StyledLoadMoreButton>
        )}
      </StyledTopicSectionContainer>
    </Container>
  );
};

export default TopicSection;
